import SlsTracker from '@aliyun-sls/web-track-browser'

/**
 * SLS 前端日志上报（WebTracking 匿名写入，无需 STS 签名）
 * - 拦截 console.log / info / warn / error，批量缓冲上报
 * - 上报请求头携带 X-Span-Id，本地缓存生成时间，超过 30 分钟重新生成
 * 前提：logstore 需开启 WebTracking 开关
 */

// ============ SLS 配置（占位常量，部署前替换） ============
const SLS_CONFIG = {
  host: 'cn-hangzhou.log.aliyuncs.com', // SLS endpoint
  project: 'ut-log-project',
  logstore: 'sem_web_tracking',
  count: 10, // 条数阈值：攒够即上报
  time: 5 // 时间阈值（秒）：到点批量上报
}

const SPAN_STORAGE_KEY = 'sls_span_id'
const SPAN_EXPIRE = 30 * 60 * 1000 // 30 分钟

/**
 * 获取 X-Span-Id：本地缓存 id 和生成时间，未过期复用，过期重新生成
 */
export function getSpanId() {
  const now = Date.now()
  try {
    const cached = JSON.parse(localStorage.getItem(SPAN_STORAGE_KEY) || 'null')
    if (cached && cached.id && now - cached.time < SPAN_EXPIRE) {
      return cached.id
    }
  } catch { /* 缓存损坏时直接重新生成 */ }

  const id = `${now.toString(16)}${Math.random().toString(16).slice(2, 10)}`.slice(0, 16)
  try {
    localStorage.setItem(SPAN_STORAGE_KEY, JSON.stringify({ id, time: now }))
  } catch { /* 存储失败不影响上报 */ }
  return id
}

function getCacheUserId(){
  const user = localStorage.getItem('user')
  if (!user) return null;
  return JSON.parse(user).sysAccount.id;
}

export function getCacheSpanId() {
  const span = localStorage.getItem(SPAN_STORAGE_KEY);
  if (!span) return null;
  return  JSON.parse(span).id;
}

let tracker = null

function getTracker() {
  if (tracker) return tracker

  tracker = new SlsTracker({
    host: SLS_CONFIG.host,
    project: SLS_CONFIG.project,
    logstore: SLS_CONFIG.logstore,
    count: SLS_CONFIG.count,
    time: SLS_CONFIG.time,
    source: 'chemical-web'
  })

  // 覆盖默认传输（sendBeacon 不支持自定义请求头），改用 fetch 注入 X-Span-Id
  // payload 格式与 SDK 默认一致：{ __logs__: [...], __topic__, __source__ }
  tracker.getOpt().sendPayload = (url, payload) =>
    fetch(`${url}?APIVersion=0.6.0`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Span-Id': getSpanId()
      },
      body: payload,
      keepalive: true // 页面卸载时（beforeunload 兜底上报）也能发出
    }).catch(() => {})

  return tracker
}

/**
 * 序列化 console 参数：Error 取堆栈，对象转 JSON
 */
function serialize(args) {
  return args.map(arg => {
    if (arg instanceof Error) return `${arg.message}\n${arg.stack || ''}`
    if (typeof arg === 'object' && arg !== null) {
      try { return JSON.stringify(arg) } catch { return String(arg) }
    }
    return String(arg)
  }).join(' ')
}

let reporting = false // 防重入：上报链路内部的 console 输出不再拦截，避免死循环

/**
 * 初始化：拦截 console 并上报到 SLS
 */
export function initSlsLogger() {
  const isProd = process.env?.NODE_ENV === 'production'
  // 非正式环境直接返回，不拦截 console 也不上报
  if (!isProd) return;

  const levels = ['log', 'info', 'warn', 'error']
  levels.forEach(level => {
    const original = console[level]
    console[level] = function (...args) {
      original.apply(console, args)
      if (reporting) return
      try {
        reporting = true
        getTracker().send({
          level,
          message: serialize(args),
          page: window.location.href,
          time: new Date().toISOString(),
          'X-Span-Id' : getCacheSpanId(),
          'X-User-Id' : getCacheUserId(),
        })
      } catch { /* 上报失败不阻塞业务 */ } finally {
        reporting = false
      }
    }
  })
}

export default { initSlsLogger }
